import React from "react";
import { AbilityUsage, AbilityResult } from "./abilities";
import { RolePlayer } from "./RoleDistributorReducer";
import { useTranslation } from "react-i18next";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  results: AbilityUsage[];
  allPlayers: RolePlayer[];
  currentPlayerId: number;
}

const AbilityResultModal: React.FC<Props> = ({
  isOpen,
  onClose,
  results,
  allPlayers,
  currentPlayerId,
}) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  // 현재 플레이어에게 표시할 결과만 필터링
  const visibleResults = results.filter(usage => {
    const result = usage.result;
    if (!result) return false;
    
    // 공개 결과는 모두에게 표시
    if (result.isPublic) return true;
    
    // 비공개 결과는 해당 플레이어에게만 표시
    return result.targetPlayerId === currentPlayerId;
  });

  // 공개 결과와 개인 결과로 분리
  const publicResults = visibleResults.filter(usage => usage.result?.isPublic);
  const privateResults = visibleResults.filter(usage => !usage.result?.isPublic);

  const getPlayerName = (playerId: number) => {
    return allPlayers.find(p => p.id === playerId)?.name || '알 수 없음';
  };

  const renderResultItem = (usage: AbilityUsage, index: number) => {
    const result = usage.result!;
    const playerName = getPlayerName(usage.playerId);
    
    return (
      <div key={index} className="result-item">
        <div className="result-header">
          <span className={`result-type ${result.type}`}>
            {result.type === 'investigation' && '🔍'}
            {result.type === 'assassination' && '🗡️'}
            {result.type === 'protection' && '🛡️'}
            {result.type === 'vote' && '🗳️'}
            {result.type === 'general' && '⚡'}
          </span>
          <span className="player-name">{playerName}</span>
          <span className={`result-status ${result.success ? 'success' : 'failed'}`}>
            {result.success ? '성공' : '실패'}
          </span>
        </div>
        <div className="result-message">
          {result.message}
        </div>
        {result.data && result.type === 'investigation' && (
          <div className="investigation-details">
            <strong>{result.data.targetName}</strong>의 역할: 
            <span className="role-reveal">{result.data.targetRole}</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="ability-result-modal-overlay" onClick={onClose}>
      <div className="ability-result-modal" onClick={(e) => e.stopPropagation()}>
        {/* 모달 헤더 */}
        <div className="result-modal-header">
          <h3>{t("RoleDistributor.abilityResults", "능력 결과")}</h3>
          <button className="close-button" onClick={onClose}>
            <i className="fas fa-times" />
          </button>
        </div>

        {/* 공개 결과 섹션 */}
        {publicResults.length > 0 && (
          <div className="result-section public-results">
            <h4>{t("RoleDistributor.publicResults", "공개 결과")}</h4>
            <div className="result-list">
              {publicResults.map((usage, index) => renderResultItem(usage, index))}
            </div>
          </div>
        )}

        {/* 개인 결과 섹션 */}
        {privateResults.length > 0 && (
          <div className="result-section private-results">
            <h4>{t("RoleDistributor.yourResults", "당신의 결과")}</h4>
            <div className="result-list">
              {privateResults.map((usage, index) => renderResultItem(usage, index + publicResults.length))}
            </div>
          </div>
        )}

        {/* 결과가 없는 경우 */}
        {visibleResults.length === 0 && (
          <div className="no-results">
            <p>{t("RoleDistributor.noResults", "표시할 결과가 없습니다.")}</p>
          </div>
        )}

        {/* 확인 버튼 */}
        <div className="result-modal-actions">
          <button className="btn-confirm" onClick={onClose}>
            {t("RoleDistributor.confirm", "확인")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AbilityResultModal;